import { html, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { EkoElement } from "@/utilities/element";
import style from "./credit-card.css?inline";

@customElement("eko-credit-card")
export class CreditCard extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  @property({ type: String }) number = "";
  @property({ reflect: true }) size: "sm" | "md" | "lg" = "sm";
  @property({ reflect: true })
  type: "visa" | "mastercard" | "amex" | "discover" | "maestro" | "jcb" | "diners-club" =
    "mastercard";

  private detectCreditCardType():
    | "visa"
    | "mastercard"
    | "amex"
    | "discover"
    | "maestro"
    | "jcb"
    | "diners-club"
    | "unknown" {
    if (!this.number) {
      return this.type;
    }

    // Remove all non-digit characters
    const cleanNumber = this.number.replace(/\D/g, "");

    // Visa: starts with 4
    if (/^4/.test(cleanNumber)) {
      return "visa";
    }

    // Maestro: starts with 50, 56-58, 60-69
    if (/^(50|5[6-8]|6[0-9])/.test(cleanNumber)) {
      return "maestro";
    }

    // Mastercard: starts with 2 or 5
    if (/^[25]/.test(cleanNumber)) {
      return "mastercard";
    }

    // American Express: starts with 34 or 37
    if (/^3[47]/.test(cleanNumber)) {
      return "amex";
    }

    // JCB: starts with 35
    if (/^35/.test(cleanNumber)) {
      return "jcb";
    }

    // Discover: starts with 6
    if (/^6/.test(cleanNumber)) {
      return "discover";
    }

    // Diners Club: starts with 30, 36, 38, or 39
    if (/^3[0689]/.test(cleanNumber)) {
      return "diners-club";
    }

    return "mastercard";
  }

  private getCard(type: string) {
    return new URL(`../../assets/credit-cards/${type}.svg`, import.meta.url).href;
  }

  private getCardName(code: string): string {
    const cardNames: Record<string, string> = {
      amex: "American Express",
      diners: "Diners Club",
      discover: "Discover",
      jcb: "JCB",
      maestro: "Maestro",
      mastercard: "Mastercard",
      visa: "Visa",
    };
    return cardNames[code] + " Credit Card" || "Credit Card";
  }

  render() {
    return html`
      <img
        src="${this.getCard(this.detectCreditCardType())}"
        class="eko-credit-card__image"
        part="image"
        alt="${this.getCardName(this.type)}" />
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-credit-card": CreditCard;
  }
}
